import React, { useEffect, useState } from "react";
import {
  Modal,
  Input,
  Button,
  Typography,
  Alert,
  Space,
  Descriptions,
  Form,
  Spin,
} from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { convertUnitWrapper } from "../../../../../actions/qc";
import "./UnitConversionModal.css";

const { Text } = Typography;

const UnitConversionModal = ({
  open,
  onCancel,
  testName,
  resultText,
}) => {
  const [targetUnit, setTargetUnit] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversionData, setConversionData] = useState(null);
  const [requestError, setRequestError] = useState(null);

  useEffect(() => {
    if (open) {
      setTargetUnit("");
      setConversionData(null);
      setRequestError(null);
      setLoading(false);
    }
  }, [open, testName, resultText]);

  const copyText = (conversionData !== null && typeof conversionData === "object")
    ? conversionData.result
    : "";

  const handleClose = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleSubmit = async () => {
    try {
      const trimmedUnit = targetUnit.trim();
      if (!trimmedUnit) {
        setRequestError("Enter a target unit.");
        setConversionData(null);
        return;
      }
      setLoading(true);
      setRequestError(null);
      const response = await convertUnitWrapper({
        test_name: testName,
        result: resultText,
        conversion_unit: trimmedUnit,
      });
      setLoading(false);
      if (response.success && !response.error) {
        setConversionData(response.data);
        // toast.success("Unit conversion successful");
      } else if (response.error && !response.success) {
        setConversionData(null);
        setRequestError(response?.data?.explanation || "");
      } else {
        setConversionData(null);
        toast.error(response.message);
      }
    } catch (error) {
      setLoading(false);
      setConversionData(null);
      toast.error("Unit conversion failed.");
    }
  };

  const handleCopyResult = async () => {
    if (copyText === "") {
      return;
    }
    try {
      if (navigator?.clipboard && navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(copyText);
        toast.success("Copied result");
      } else {
        toast.error("Could not copy to clipboard.");
      }
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const renderConversionOutput = () => {
    if (conversionData === null || typeof conversionData !== "object") return null;

    const displayedResult = conversionData.result ?? "";
    const displayedExplanation = typeof conversionData.explanation === "string" && conversionData.explanation.trim() !== ""
      ? conversionData.explanation.trim()
      : "-";
    const showRefreshLoading = loading;

    return (
      <div className="UnitConversionModal__OutputSection">
        <Text strong className="UnitConversionModal__OutputHeading">
          Conversion output
        </Text>
        <Descriptions
          bordered
          size="small"
          column={1}
          className="UnitConversionModal__OutputDescriptions"
        >
          <Descriptions.Item label="Converted Result">
            <div className="UnitConversionModal__ConvertedRow">
              <div
                className={
                  showRefreshLoading
                    ? "UnitConversionModal__ConvertedValueCell UnitConversionModal__ConvertedValueCell--loading"
                    : "UnitConversionModal__ConvertedValueCell"
                }
                aria-busy={showRefreshLoading}
              >
                <span className="UnitConversionModal__ConvertedValue">
                  {displayedResult}
                </span>
                {showRefreshLoading && (
                  <div className="UnitConversionModal__ConvertedValueLoading">
                    <Spin size="small" />
                  </div>
                )}
              </div>
              <Button
                type="primary"
                size="small"
                icon={<CopyOutlined />}
                onClick={handleCopyResult}
                disabled={loading || copyText === ""}
                className="UnitConversionModal__CopyResultButton"
              />
            </div>
          </Descriptions.Item>
          {displayedExplanation !== "" && (
            <Descriptions.Item label="Explanation">
              <Text type="secondary" className="UnitConversionModal__ExplanationValue">
                {displayedExplanation}
              </Text>
            </Descriptions.Item>
          )}
        </Descriptions>
      </div>
    );
  };

  return (
    <Modal
      centered
      title="Convert units"
      width={560}
      open={open}
      onCancel={handleClose}
      closable={!loading}
      maskClosable={!loading}
      rootClassName="UnitConversionModal"
      footer={[
        <Button key="close" onClick={handleClose} disabled={loading}>
          Close
        </Button>,
        <Button
          key="convert"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          disabled={loading}
        >
          Convert
        </Button>,
      ]}
    >
      <Space direction="vertical" size="middle" className="UnitConversionModal__Stack">
        <div>
          <Text strong className="UnitConversionModal__SectionHeading">
            Test values
          </Text>
          <Descriptions
            bordered
            size="small"
            column={1}
            className="UnitConversionModal__InputDescriptions"
          >
            <Descriptions.Item label="Test name">
              {testName}
            </Descriptions.Item>
            <Descriptions.Item label="Current result">
              {resultText}
            </Descriptions.Item>
          </Descriptions>
        </div>
        <div className="UnitConversionModal__TargetField">
          <Text strong className="UnitConversionModal__SectionHeading">
            Target unit
          </Text>
          <Form.Item
            className="UnitConversionModal__TargetFormItem"
            validateStatus={requestError ? "error" : undefined}
            help={requestError || undefined}
          >
            <Input
              value={targetUnit}
              onChange={(event) => {
                setTargetUnit(event.target.value);
                if (requestError) {
                  setRequestError(null);
                }
              }}
              placeholder="Enter target unit only (e.g. mg/L)"
              allowClear
              disabled={loading}
              className="UnitConversionModal__TargetInput"
            />
          </Form.Item>
        </div>
        {renderConversionOutput()}
        {(conversionData && conversionData !== null) && (
          <Alert
            type="info"
            showIcon
            message="You can copy the returned result and paste it into the result field using the existing edit flow."
          />
        )}
      </Space>
    </Modal>
  );
};

export default UnitConversionModal;
